// #66 Health: Req health (DC-17) = the numeric score in a ring stroked with the band's text twin, the band word as a
// pill (Green ok · Yellow warn · Red red), the transparent drivers as check rows, and his foot line "Health informs
// attention; it does not make employment decisions." No person is scored and no letter grade is drawn. Each driver row
// says what moved, what it measured and the points it moved the score ("3 · −15"); only a driver that costs points
// warns and links to the screen that clears it, and a clear one reads neutral, with no glyph and no link (RQ-14). The
// accessible name counts the drivers that cost points. `sm` is the list-row form: a 28 px ring (Ring draws a score of
// 100 at 44) and the band word, one image named like the large ring. Loading draws a skeleton ring and rows; no
// drivers is one ok row.

import { CheckRow, Checklist, Pill, SectionTitle, Skeleton, SURFACE_LABELS, type CheckStatus } from "../core";
import { HEALTH_BAND_LABELS, RULE_LABELS } from "../labels";
import { cx } from "../lib/cx";
import { useSvgId } from "../lib/useSvgId";
import { METRIC_TEMPLATES } from "./labels";
import { Ring, type RingBand } from "./Ring";
import type { Hs } from "../lib/hs";

export type HealthBand = "green" | "yellow" | "red";

const RING_BAND: Readonly<Record<HealthBand, RingBand>> = { green: "ok", yellow: "warn", red: "red" };

export interface HealthDriver {
  /** `ok` is a clear driver, drawn neutral; any other status costs points and warns. */
  status: CheckStatus;
  /** The driver's name (§7: *Overdue review*, *HM responsiveness* …). */
  text: string;
  /** What it measured, already in words ("3", "4 days", "25 / 30 days"). */
  value?: string | undefined;
  /** The points it moved the score: below zero costs points, zero is clear. */
  points?: number | undefined;
  /** One line of cause. */
  sub?: string | undefined;
  /** The deep link to the screen that clears it (drawn only on a driver that costs points). */
  href?: string | undefined;
  hs?: Hs | undefined;
}

export interface HealthProps {
  /** 0–100. */
  score: number;
  band: HealthBand;
  drivers?: readonly HealthDriver[] | undefined;
  size?: "lg" | "sm" | undefined;
  loading?: boolean | undefined;
  can?: boolean | undefined;
  className?: string | undefined;
}

/** Points as the score moved: "−15", "+5", "0". */
const signed = (points: number): string => points < 0 ? `−${Math.abs(points)}` : points > 0 ? `+${points}` : "0";

/** A driver's line: what it measured, its points and its cause, the parts it has. */
function lineOf({ value, points, sub }: HealthDriver): string | undefined {
  const parts = [value, points === undefined ? undefined : signed(points), sub].filter((part): part is string => !!part);
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

/** A clear driver: its words and line on an empty glyph cell, never a link (CheckRow always draws a glyph). */
function ClearRow({ driver }: { driver: HealthDriver }) {
  const line = lineOf(driver);
  return (
    <li className="mc-chk mc-health-clear">
      <span className="mc-ci" aria-hidden="true" />
      <span className="mc-cx">
        <span className="mc-chk-t">{driver.text}</span>
        {line !== undefined ? <span className="mc-chk-s">{line}</span> : null}
      </span>
    </li>
  );
}

export function Health({ score, band, drivers = [], size = "lg", loading, can, className }: HealthProps) {
  const titleId = `${useSvgId()}-drv`;
  if (can === false) return null;
  const word = HEALTH_BAND_LABELS[band];
  const costing = drivers.filter((driver) => driver.status !== "ok").length;
  const name = METRIC_TEMPLATES.healthName({ n: Math.round(score), band: word, k: costing });
  if (size === "sm") {
    if (loading) return <span className={cx("mc-health-sm", className)} aria-busy="true"><Skeleton width={28} height={28} shape="pill" /></span>;
    return (
      <span className={cx("mc-health-sm", `mc-health-${band}`, className)} role="img" aria-label={name}>
        <Ring size={28} n={score} band={RING_BAND[band]} label={name} />
        <span className="mc-health-w" aria-hidden="true">{word}</span>
      </span>
    );
  }
  if (loading) {
    return (
      <div className={cx("mc-health", className)} aria-busy="true">
        <div className="mc-health-score"><Skeleton width={72} height={72} shape="pill" /><Skeleton width={56} height={24} shape="pill" /></div>
        <div className="mc-health-drv"><Skeleton lines={3} /></div>
      </div>
    );
  }
  return (
    <div className={cx("mc-health", className)}>
      <div className="mc-health-score">
        <Ring size={72} n={score} band={RING_BAND[band]} label={name} />
        <Pill>{word}</Pill>
      </div>
      <div className="mc-health-drv">
        <SectionTitle id={titleId}>{SURFACE_LABELS.drivers}</SectionTitle>
        <Checklist labelledBy={titleId}>
          {drivers.length === 0
            ? <CheckRow status="ok">{HEALTH_BAND_LABELS.green}</CheckRow>
            : drivers.map((driver, index) => driver.status === "ok"
              ? <ClearRow key={index} driver={driver} />
              : <CheckRow key={index} status={driver.status} sub={lineOf(driver)} href={driver.href} hs={driver.hs}>{driver.text}</CheckRow>)}
        </Checklist>
      </div>
      <p className="mc-health-foot">{RULE_LABELS.healthInformsAttention}</p>
    </div>
  );
}
